import { AcademicStatus } from "../../modules/common/shared/types";

// TypeScript type aliases are not objects, they don't exist at runtime.
// use a const object (if you want dot notation):
// export type AcademicYearStatusType = "PLANNED" | "ACTIVE" | "CLOSED";

export const AcademicYearStatus = {
  ACTIVE: "ACTIVE",
  CLOSED: "CLOSED",
  PLANNED: "PLANNED",
  INITIALIZED: "INITIALIZED",
} as const;

export type AcademicYearStatus = (typeof AcademicYearStatus)[keyof typeof AcademicYearStatus];

export interface AcademicYear {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  status: AcademicYearStatus;
}

export interface EnrollmentInitializationResult {
  created: number;
  skipped: number;
  failed: number;
}

export interface AcademicYearInitializationResult {
  academicYearDto: AcademicYear;
  enrollmentInitializationResult: EnrollmentInitializationResult;
}

export interface EnrollmentSyncResult {
  activated: number;
  failed: number;
}
export interface StudentEnrollmentState {
  admissionNo: string;
  name: string;
  grade: string;
  section: string;
}
export interface AcademicYearSyncResult {
  academicYearDto: AcademicYear;
  enrollmentSyncResult: EnrollmentSyncResult;
  activated: StudentEnrollmentState[];
  failed: StudentEnrollmentState[];
}

export interface Section {
  id: string;
  code: string;
  name: string;
  description: string;
}

// A section attached to a grade, as returned inside GET /grades.
export interface GradeSection {
  id: number;
  code: string;
  name: string;
}

export interface Grade {
  id: number;
  code: string;
  name: string;
  promoteTo: string | null;
  numericValue: number | null;
  sections: GradeSection[]; // active sections; the backend omits the key when empty
}

// POST /grades — the code is set only here.
export interface GradeCreateRequest {
  code: string;
  name: string;
  promoteTo: string;
  numericValue?: number;
  sectionCodes: string[];
}

export interface Subject {
  code: string;
  name: string;
  description: string;
}

// Class Subjects screen — GET /class-subjects returns everything the page needs.
export interface ClassSubjectsSubject {
  id: number;
  code: string;
  name: string;
}

// One class: a section of a grade, identified by gradeSectionId, with its subject codes.
export interface ClassSubjectsSection {
  gradeSectionId: number;
  sectionId: number;
  code: string;
  name: string;
  subjectCodes: string[];
}

export interface ClassSubjectsGrade {
  id: number;
  code: string;
  name: string;
  sections: ClassSubjectsSection[]; // empty when the grade has no sections yet
}

export interface ClassSubjectsData {
  subjects: ClassSubjectsSubject[];
  grades: ClassSubjectsGrade[];
}

// Year Transition wizard — backend drives progression via a single envelope
// shape returned from every transition endpoint (see /academic-years/transition/*).
export const TransitionStage = {
  PROMOTE: 1,
  YEAR_CREATED: 2,
  YEAR_CLOSED: 3,
  FEE_STRUCTURE: 4,
  INITIALIZED: 5,
  COMPLETE: 6,
} as const;

export type TransitionStage = (typeof TransitionStage)[keyof typeof TransitionStage];

export interface TransitionStudent {
  admissionNo: string;
  name: string;
  guardianPhone: string;
  totalDue: number;
}

export interface TransitionPromotionInput {
  admissionNo: string;
  academicStatus: AcademicStatus;
}

export interface TransitionFeeItem {
  name: string;
  currentFee: number;
}

export interface TransitionResponse {
  stage: TransitionStage;
  success: boolean;
  grade?: string;
  section?: string;
  data: {
    students?: TransitionStudent[];
    feeStructure?: TransitionFeeItem[];
    academicYear?: { name: string; status: string };
  };
  message: string;
}

// ---------- Timetable: bell schedule and grade periods (docs/timetable.md) ----------

// One row of the school's bell schedule. Times are "HH:mm". periodNumber = order in the day
// (breaks included); the server renumbers by start time on save.
export interface Period {
  id?: number; // missing on a period that hasn't been saved yet
  periodNumber: number;
  name: string;
  startTime: string;
  endTime: string;
  isBreak: boolean;
}

export const WORKING_DAYS = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"] as const;
export type DayOfWeek = (typeof WORKING_DAYS)[number];

// A grade's periods on each day: period ids. A day with no periods is not a working day for the grade.
export type GradeDayPeriods = Partial<Record<DayOfWeek, number[]>>;

// ---------- Timetable: a class's week (Academics → Timetable) ----------

// One teaching assignment of the class in the active year: a subject taught by a faculty.
export interface TimetableAssignment {
  id: number; // faculty_assignment id
  subjectCode: string;
  subjectName: string;
  facultyId: number;
  facultyName: string;
}

export interface TimetableSlot {
  day: DayOfWeek;
  periodId: number;
  assignmentId: number;
}

// The class's faculty already teaching another class at that day + period.
export interface FacultyBusy {
  facultyId: number;
  day: DayOfWeek;
  periodId: number;
  className: string; // e.g. "6 - B"
}

// GET /timetable/classes/{gradeSectionId}: everything the class grid needs, in one call.
export interface ClassTimetable {
  academicYear: string | null; // null when there is no active year
  gradeSectionId: number;
  periods: Period[]; // the bell schedule, by start time
  days: GradeDayPeriods; // the grade's periods per day
  assignments: TimetableAssignment[];
  slots: TimetableSlot[];
  busy: FacultyBusy[];
}

// ---------- Timetable: one faculty's week (By Faculty, My Timetable) ----------

export interface FacultySlot {
  day: DayOfWeek;
  periodId: number;
  gradeSectionId: number;
  className: string; // e.g. "Eight - A"
  subjectCode: string;
  subjectName: string;
}

// GET /timetable/faculties/{facultyId} (admin) and GET /timetable/me (the logged-in faculty).
export interface FacultyTimetable {
  academicYear: string | null; // null when there is no active year
  facultyId: number;
  facultyName: string;
  periods: Period[]; // the bell schedule, by start time
  days: DayOfWeek[]; // days any grade has periods (the school's working days)
  slots: FacultySlot[];
}
